import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { CcIcon } from '@utp/components-react';
import styled from 'styled-components';
import OutsideClickHandler from 'react-outside-click-handler';
import IDHelpers from '../../core/helpers/IDHelper';
import { Creators as signOutActions } from '../../domain/useCases/login/signOutUseCase';
import constants from '../../core/constants';
import TextHelper from '../../core/helpers/TextHelper';

interface IMenuList {
  labelText?: string;
  iconName?: string;
  modeClick: boolean;
  handleClick?: () => void;
  urlClick?: string;
}

interface IUserData {
  userName?: string;
  userAvatar?: string;
}

interface Props {
  menu?: Array<IMenuList>;
  user?: IUserData;
}

const UserMenuWrapper = styled.div`
  text-align: center;
  color: var(--neutral-03);
  display: inline-flex;
  font-size: 16px;
`;

const UserMenuElement = styled(UserMenuWrapper)`
  display: none;
  &::before {
    content: '';
    width: 1px;
    height: 36px;
    background-color: #7a959f;
    align-self: center;
  }
  @media (min-width: 992px) {
    display: inline-flex;
  }

  .usermenu {
    color: var(--text-text02);
  }
  .usermenu .button.sc-cc-button {
    background-color: #b0c0ca;
  }

  .border-b {
    min-height: 90px;
  }

  .usermenu > * > .flex {
    display: block;
    float: right;
  }

  .relative {
    position: relative;
  }

  .shadow {
    box-shadow: 0 4px 16px -8px #b0c0ca;
  }

  .overflow-hidden {
    overflow: hidden;
  }

  .rounded-sm {
    border-radius: 0.4rem;
  }

  .bg-neutral-04 {
    background-color: var(--neutral-04);
  }

  .cursor-pointer {
    cursor: pointer;
  }

  .py-xlg {
    border-color: var(--background-01);
    padding-top: 2rem;
    padding-bottom: 2rem;
  }

  .mt-sm {
    margin-top: 0.8rem;
  }
  .text-body {
    min-width: 20.8rem;
    width: 100%;
    display: block;
  }

  .user__element {
    display: flex;
    height: 48px;
    grid-gap: 0px;
  }
  .user__element cc-icon {
    color: var(--neutrals-neutral02);
  }
  .user__element-label {
    // line-height: 48px;
    text-transform: capitalize;
  }

  .user__element-type {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: #4f6168;
    text-align: start;
  }

  .user__element-avatar {
    width: 40px;
    margin: 4px 13px 4px 16px;
    height: 40px;
    overflow: hidden;
    border-radius: 50%;
  }
  .user__element-icon {
    color: var(--text-text02);
    height: 24px;
    display: table-cell;
    vertical-align: middle;
  }
  .user__element-toggle {
    display: table;
    height: 48px;
    outline: none;
    padding: 12px 0px;
  }

  .usermenu__toggle-menu {
    position: absolute;
    top: 68px;
    right: 0px;
    display: none;
    z-index: 99;
  }

  .usermenu__toggle-user {
    position: relative;
    padding: 0px 0px 0px 15px;
    outline: none;
  }

  .usermenu__toggle-user:focus {
    outline: none;
  }

  .menu__element-label {
    line-height: 40px;
    display: block;
    float: left;
  }

  .menu__element-link {
    line-height: 40px;
    display: flex;
    flex-flow: row;
    width: 100%;
    color: var(--text-text01);
  }

  .menu__element-link:hover {
    color: var(--text-text02);
    background-color: var(--background-background03);
    font-weight: bold;
  }

  .menu__element-link cc-icon {
    display: flex;
    align-items: center;
    align-content: center;
    color: var(--neutrals-neutral01);
  }
  .menu__element-link:hover cc-icon {
    color: var(--neutrals-neutral02);
  }
  .usermenu__toggle--active .usermenu__toggle-menu {
    display: block;
  }

  .menu__element-icon {
    display: block;
    height: 40px;
    padding: 4px 0px 4px;
    float: left;
  }

  .usermenu__toggle-menu-container {
    padding: 8px 0px;
    display: block;
    float: left;
  }
`;

function UserMenu(props: Readonly<Props>) {
  const { menu, user } = props;
  const menuList = menu;
  const userData = user;
  const [openmenu, setOpenmenu] = useState(false);
  const menuToggle = () => {
    setOpenmenu(!openmenu);
  };

  return (
    <UserMenuElement data-testid="UserMenu">
      <UserMenuWrapper>
        {menuList && menuList.length ? (
          <div className="usermenu">
            <div>
              <div
                className={`flex ml-auto usermenu__toggle ${
                  openmenu ? 'usermenu__toggle--active' : ''
                }`}
              >
                <div role="menu" className="usermenu__toggle-user">
                  {userData?.userName && (
                    <div className="user__element" key={IDHelpers.generateID()}>
                      <div>
                        <span className="user__element-label">
                          Hola,{' '}
                          <b>
                            {userData?.userName &&
                              TextHelper.capitalize(
                                userData?.userName.split(' ')[0]
                              )}{' '}
                          </b>
                        </span>
                        <p className="user__element-type">Estudiante</p>
                      </div>
                      <img
                        className="user__element-avatar"
                        alt={userData?.userName}
                        src={userData?.userAvatar}
                      />
                      <div
                        role="button"
                        tabIndex={0}
                        className="user__element-toggle"
                        onClick={() => menuToggle()}
                        onKeyDown={() => {}}
                      >
                        <CcIcon name="chevron-down" />
                      </div>
                    </div>
                  )}
                  {!userData?.userName && (
                    <img
                      className="user__element-avatar"
                      alt={userData?.userName}
                      src={userData?.userAvatar}
                    />
                  )}
                  <OutsideClickHandler
                    onOutsideClick={() => {
                      setOpenmenu(false);
                    }}
                  >
                    <div className="usermenu__toggle-menu">
                      <div className="bg-neutral-04 shadow relative overflow-hidden flex flex-col rounded-sm overflow-hidden">
                        <div className="usermenu__toggle-menu-container">
                          {menuList.map((list: any) =>
                            list.modeClick === false ? (
                              <a
                                key={IDHelpers.generateID()}
                                href={list.urlClick}
                                target={list.targetBlank ? '_blank' : '_self'}
                                rel="noreferrer"
                                onClick={() => list.handleClick}
                                onKeyDown={() => {}}
                                className="px-lg text-body menu__element-link"
                              >
                                <img src={list.icon} alt="icon" />
                                <span className="ml-sm menu__element-label">
                                  {list.labelText}
                                </span>
                              </a>
                            ) : (
                              <button
                                type="button"
                                key={IDHelpers.generateID()}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                                onClick={() => {
                                  setOpenmenu(false);
                                  // onClickLogout();
                                  list.onClick();
                                }}
                                // href={list.urlClick}
                                // onClick={() => list.handleClick}
                                className="px-lg text-body menu__element-link"
                              >
                                {/* <CcIcon name="log-out" /> */}
                                <img src={list.icon} alt="icon" />
                                <span className="ml-sm menu__element-label">
                                  {list.labelText}
                                </span>
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </OutsideClickHandler>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div>Cargando... </div>
        )}
      </UserMenuWrapper>
    </UserMenuElement>
  );
}

UserMenu.defaultProps = {
  menu: [
    {
      labelText: 'Mi Perfil',
      iconName: 'user',
      modeClick: true,
      handleClick: () => {},
      urlClick: '#'
    },
    {
      labelText: 'Cerrar sesión',
      iconName: 'log-out',
      modeClick: true,
      handleClick: () => {
        const dispatch = useDispatch();
        dispatch(signOutActions.signOut());
      },
      urlClick: '#'
    }
  ],
  user: {
    userName: 'UserName',
    photo: constants.DEFAULT_PHOTO,
    userAvatar: constants.DEFAULT_PHOTO
  }
};

export default UserMenu;
